<template>
  <div class="ca-actions-module-bg" :class="highlightBackgroundClass">
    <div class="ca-bullet-container">
      <div class="ca-actions-module pt-size-9 pb-size-10">
        <nav aria-label="Highlight Section" class="flex flex-col gap-size-7">
          <CaModuleMessage
            :label="model.label"
            :description="model.description"
            :title="model.title"
            :title-heading-type="model.titleHeadingType || 'H2'"
            :description-heading-type="model.descriptionHeadingType"
          />
          <div class="ca-actions-module__body">
            <div
              v-if="cards.length"
              class="ca-quick-action-cards grid grid-cols-1 gap-size-5"
            >
              <CaQuickActionCard
                v-for="(card, index) in cards"
                :key="index"
                class="ca-quick-action-cards__card"
                :class="{
                  'ca-quick-action-cards__card--full': isFullWidth(index),
                }"
                :title="card.title"
                :title-heading-type="card.titleHeadingType"
                :description="card.description"
                :description-heading-type="card.descriptionHeadingType"
                :backgroundColor="normalizeColor(card.color)"
                :image="card.iconUrl"
                :avatarBackgroundColorClass="card.iconBackgroundColor"
                :url="card.url"
                :target="card.target"
                :isBackgroundGreen="isBackgroundGreen"
              />
            </div>
            <img
              class="ca-actions-module__media rounded-size-5"
              :src="imageSrc"
              :alt="model.label"
              loading="lazy"
              @error="onMediaError"
            />
          </div>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
const isDev = import.meta.env.DEV;
</script>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import CaQuickActionCard from "../../base/cards/CaQuickActionCard.vue";
import CaModuleMessage from "../../base/moduleMessage/CaModuleMessage.vue";
// Placeholders (SVG) importados como raw e convertidos em data URI (mesma
// lógica/fallback do CaMediaContent): desktop e mobile.
import placeholderRaw from "@assets/media_content_placeholder.svg?raw";
import mobilePlaceholderRaw from "@assets/media_content_placeholder_mobile.svg?raw";
const placeholderImage = `data:image/svg+xml,${encodeURIComponent(placeholderRaw)}`;
const mobilePlaceholderImage = `data:image/svg+xml,${encodeURIComponent(mobilePlaceholderRaw)}`;

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

// Para import dinâmico em dev, evitando incluir o mock no bundle de produção
const modelRef = ref(props.model ?? null);
const model = computed(() => modelRef.value || {});

const cards = computed(() => model.value.cards ?? []);

// True em viewports mobile (< 768px); escolhe o placeholder correspondente.
const isMobile = ref(false);

/** Placeholder ativo consoante o viewport (mobile vs. desktop). */
const activePlaceholder = computed(() =>
  isMobile.value ? mobilePlaceholderImage : placeholderImage,
);

/** Whether an image source is a non-empty, usable string. */
function isValidSrc(src) {
  return typeof src === "string" && src.trim().length > 0;
}

/** Image source with placeholder fallback when empty/invalid. */
const imageSrc = computed(() =>
  isValidSrc(model.value.imageUrl)
    ? model.value.imageUrl
    : activePlaceholder.value,
);

/** Swap a broken image for the placeholder. */
function onMediaError(event) {
  const img = event.target;
  if (img && img.src !== activePlaceholder.value) {
    img.src = activePlaceholder.value;
  }
}

const backgroundVariants = {
  laranja: "laranja",
  verde: "verde",
  branco: "branco",
};

/** Normalize a model color (e.g. "verde") to the base card variant. */
function normalizeColor(color) {
  return backgroundVariants[String(color || "").toLowerCase()] ?? "branco";
}

/**
 * When the number of cards is odd, the last card spans the full width
 * (both columns). Covers the 1-card and 3-card cases.
 */
function isFullWidth(index) {
  const total = cards.value.length;
  return total % 2 === 1 && index === total - 1;
}

/** Highlight section background variants coming from the model. */
const highlightBackgroundVariants = {
  green: "green",
  solarized: "solarized",
};

/** Background applied to the .ca-actions-module, coming from the model. */
const highlightBackgroundClass = computed(() => {
  const variant = String(model.value.backgroundColor || "").toLowerCase();
  const mapped = highlightBackgroundVariants[variant];
  return mapped ? `ca-actions-module--${mapped}` : "";
});

/** True when the section background is green, propagated to the cards. */
const isBackgroundGreen = computed(
  () => String(model.value.backgroundColor || "").toLowerCase() === "green",
);

onMounted(async () => {
  // Em desenvolvimento, se não receber um model via props, carrega o mock para facilitar testes
  if (isDev && !modelRef.value) {
    const { mockActionCards4 } = await import("./mocks/actionsModule.js");
    modelRef.value = mockActionCards4;
  }
});

// ---------------------------------------------------------------------------
// Deteta viewports mobile para escolher o placeholder adequado (mesma lógica
// do CaMediaContent).
// ---------------------------------------------------------------------------
let mobileQuery = null;
function handleMobileChange(event) {
  isMobile.value = event.matches;
}

onMounted(() => {
  if (typeof window !== "undefined" && window.matchMedia) {
    mobileQuery = window.matchMedia("(max-width: 767px)");
    isMobile.value = mobileQuery.matches;
    mobileQuery.addEventListener("change", handleMobileChange);
  }
});

onBeforeUnmount(() => {
  if (mobileQuery) {
    mobileQuery.removeEventListener("change", handleMobileChange);
    mobileQuery = null;
  }
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Section padding (mobile first). Base vertical padding (size-9) is applied via
// DS utility classes in the template. The DS stylesheet does not emit md:/lg:
// padding variants, so tablet/desktop overrides use media queries with DS
// spacing tokens (var(--size-*)).
// ---------------------------------------------------------------------------
@media (min-width: 768px) {
  .ca-actions-module {
    padding-top: var(--size-9);
    padding-bottom: var(--size-9);
  }
}

@media (min-width: 1024px) {
  .ca-actions-module {
    padding-top: var(--size-11);
    padding-bottom: var(--size-11);
  }
}

// ---------------------------------------------------------------------------
// Highlight section background variants (from the model's backgroundColor).
// Applied to a full-width wrapper so the colour spans edge-to-edge, outside
// the horizontal padding of .ca-bullet-container.
//
// Each variant also re-tints the shimmer skeletons via the
// --ca-shimmer-from / --ca-shimmer-to custom properties, which cascade down
// to every child component's self-contained shimmer element.
// ---------------------------------------------------------------------------
.ca-actions-module--green {
  background: var(--ca-color-backgrounds-container-high);
  --ca-shimmer-angle: 270deg;
  --ca-shimmer-from: var(
    --Colors-CAOnBackground-Inverted_10,
    rgba(255, 255, 255, 0.1)
  );
  --ca-shimmer-to: var(--Colors-CATheme-Primary_20, rgba(6, 101, 66, 0.2));
}

.ca-actions-module--solarized {
  background: var(--ca-color-backgrounds-container);
  --ca-shimmer-angle: 90deg;
  --ca-shimmer-from: var(--Colors-CAEmpresas-SolarizedDark, #e5e3df);
  --ca-shimmer-to: var(--Colors-CAContainer-BGContainer, #f6f7f2);
}

// ---------------------------------------------------------------------------
// Container (mobile first) — layout uses DS utility classes in the template
// (flex, flex-col, p-size-4, gap-size-3, rounded-size-4).
// ---------------------------------------------------------------------------
// Odd number of cards: the last one spans both columns (mobile 2-col grid).
.ca-quick-action-cards__card--full {
  grid-column: 1 / -1;
}

// ---------------------------------------------------------------------------
// Body: mobile empilha (cards em cima, imagem em baixo). Em tablet/desktop a
// imagem fica ao lado dos cards (à esquerda), alinhada em altura com a grelha.
// ---------------------------------------------------------------------------
.ca-actions-module__body {
  display: flex;
  flex-direction: column;
  gap: var(--size-5);
}

// Imagem (mobile first): mesma aparência do antigo CaMediaContent (cover,
// cantos arredondados via utilitária, aspect-ratio 3/4 e fundo do placeholder).
.ca-actions-module__media {
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  max-height: 500px;
  object-fit: cover;
  background: var(--ca-color-neutrals-on-background-low-10);
}

@media (min-width: 768px) {
  .ca-actions-module__body {
    flex-direction: row;
    align-items: stretch;
    gap: var(--size-5);
  }

  // Imagem à esquerda; estica em altura (sem o aspect-ratio/max-height próprios)
  // para acompanhar a grelha de cards.
  .ca-actions-module__body .ca-actions-module__media {
    order: -1;
    flex: 1 1 0;
    min-width: 0;
    min-height: 0;
    aspect-ratio: auto;
    max-height: none;
  }

  .ca-actions-module__body .ca-quick-action-cards.ca-quick-action-cards {
    flex: 2 1 0;
    min-width: 0;
  }
}

@media (min-width: 1024px) {
  .ca-actions-module__body {
    gap: var(--size-5);
  }
}

// Tablet (>= 768px): two columns, keeping the previous behaviour.
// The doubled class raises specificity to (0,3,0) so it beats the DS
// `.ds-scope .grid-cols-1` utility (0,2,0), which is always active and would
// otherwise force a single column at every breakpoint.
@media (min-width: 768px) {
  .ca-quick-action-cards.ca-quick-action-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--size-4);
  }
}

@media (min-width: 1024px) {
  // Desktop padding uses the DS spacing token (the lg:p-size-8 utility
  // isn't emitted by the DS stylesheet nor by the local Tailwind config).
  // Same doubled-class specificity bump as the tablet rule; being later in
  // source order it also wins over the tablet rule at desktop widths.
  .ca-quick-action-cards.ca-quick-action-cards {
    // Ao lado da imagem os cards ficam sempre em 2 colunas (grelha 2xN).
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--size-5);
  }
}
</style>
